import axios from "axios";
import { Constants, taxonomyTypes, VENDOR_CONFIG, CookieService, MediaOpsService, authansid, AlertMessagesService, Utils } from "../../../shared";

export const VendorMappingService = {

    /**
     * Gets all vendor mappings
     * @function getMappings
     * @returns {[object]}
     * Array of objects, further documentation available at:
     * https://devmediaops-api.annalect.com/dev/docs
    */ 
    getMappings: () => {
        return axios
            .get(
                Constants.vendorMappingUrl,
                {
                    headers: {
                        ansid: authansid,
                        clientid: Utils.getRouteParam("clientid")
                    }
                }
            )
            .then(
                (response) => {
                    return response;
                }
            );
    },

    /**
     * Gets all planned vendor mappings for given publishers
     * @function getPlannedMappings
     * @returns {[object]}
     * Array of objects, further documentation available at:
     * https://devmediaops-api.annalect.com/dev/docs
    */ 
    getPlannedMappings: (publishers, sourceSystem) => {
        return axios
            .get(
                Constants.plannedVendorMappingUrl + Utils.getRouteParam("clientid") + "/vendor-mappings",
                {
                    params: {type: "Planned", publishers: publishers, source_system: sourceSystem},
                    headers: { ansid: authansid }
                }
            )
            .then(
                (response) => {
                    return response;
                }
            );
    },

    /**
        * Creates a new mapping.
        * @function addMappings
        * @param {object} payload - Mapping object.
        * @returns {object}
        * Object, further documentation available at:
        * https://devmediaops-api.annalect.com/dev/docs
    */
     addMappings: (payload) => {
        return axios
            .post(
                Constants.vendorMappingUrl,
                payload,
                {
                    headers: {
                        ansid: authansid,
                        clientid: Utils.getRouteParam("clientid"),
                    }
                }
            )
            .then(
                (response) => {
                    return response;
                }
            );
    },

    /**
     * Updates an existing mapping.
     * @function updateMappings
     * @param {object} payload - Mapping object.
     * @returns {object}
     * Object, further documentation available at:
     * https://devmediaops-api.annalect.com/dev/docs
    */ 
    updateMappings: (payload) => {
        return axios
            .put(
                Constants.vendorMappingUrl,
                payload,
                {
                    headers: {
                        ansid: authansid,
                        clientid: Utils.getRouteParam("clientid"),
                    }
                }
            )
            .then(
                (response) => {
                    return response;
                }
            );
    },

    /**
     * Combines enumeration data and MediaOcean supplier data for dropdown options.
     * @async
     * @function getDropdownOptions
     * @returns {Promise<Object>} Combined dropdown options from enumeration and MediaOcean data
     * @throws {Error} When enumeration data fetch fails
     */
    getDropdownOptions: async () => {
        try {
            const [enumResponse, mediaOceanData] = await Promise.allSettled([
                axios.get(Constants.enumerationsUrl, {
                    headers: {
                        ansid: authansid,
                        clientid: Utils.getRouteParam("clientid"),
                    }
                }),
                VendorMappingService.getMediaOceanData()
            ]);

            if (enumResponse.status === 'rejected') {
                AlertMessagesService.showMultipleAlerts([{
                    type: 'danger',
                    retryBtnText: 'Retry',
                    retryBtnEvent: VendorMappingService.getDropdownOptions,
                    title: 'Failed to fetch enumeration data',
                    bodyMsgArr: [enumResponse.reason?.message || 'Unknown error occurred']
                }]);
                throw enumResponse.reason;
            }

            const response = enumResponse.value;
            
            if (response?.data && Array.isArray(response.data)) {
                response.data.forEach((element) => {
                    if (element?.name === VENDOR_CONFIG.MEDIA_OCEAN.NAME && mediaOceanData.status === 'fulfilled') {
                        element.values = element.values || [];
                        const originalElementValues = new Set(element.values);
                        
                        if (mediaOceanData.value?.items) {
                            mediaOceanData.value.items
                                .filter(item => item && typeof item === 'string')
                                .forEach(item => originalElementValues.add(item));
                        }
                        
                        element.values = Array.from(originalElementValues);
                    }
                });
            }

            return response;
        } catch (error) {
            AlertMessagesService.showMultipleAlerts([{
                type: 'danger',
                retryBtnText: 'Retry',
                retryBtnEvent: VendorMappingService.getDropdownOptions,
                title: 'Error fetching dropdown options',
                bodyMsgArr: [error?.message || 'Unknown error occurred']
            }]);
            throw error;
        }
    },

    /**
     * Gets and processes MediaOcean supplier data from taxonomy service.
     * @async
     * @function getMediaOceanData
     * @returns {Promise<{name: string, items: Array<string>}>} Object containing processed supplier names
     * @property {string} name - The name of the vendor (MediaOcean)
     * @property {Array<string>} items - Array of processed supplier names
     */
    getMediaOceanData: async () => {
        try {
            const backupResult = new Set();
            const backupResponse = await MediaOpsService.getTaxonomy(taxonomyTypes['defaultplacement']);

            if (!backupResponse?.fields || !Array.isArray(backupResponse.fields)) {
                AlertMessagesService.showMultipleAlerts([{
                    type: 'warning',
                    title: 'Invalid Taxonomy Data',
                    bodyMsgArr: [VENDOR_CONFIG.MEDIA_OCEAN.ERROR_MESSAGES.INVALID_TAXONOMY]
                }]);
                return { name: VENDOR_CONFIG.MEDIA_OCEAN.NAME, items: [] };
            }

            const supplierCodeField = backupResponse.fields.find(
                field => field && field.name === VENDOR_CONFIG.MEDIA_OCEAN.SUPPLIER_FIELD_NAME
            );

            if (supplierCodeField?.fielditems) {
                supplierCodeField.fielditems.forEach(item => {
                    if (item?.name && item?.code) {
                        try {
                            const itemName = item.name.replace(item.code + " ", "").trim();
                            if (itemName) backupResult.add(itemName);
                        } catch (e) {
                            AlertMessagesService.showMultipleAlerts([{
                                type: 'warning',
                                title: 'Item Processing Error',
                                bodyMsgArr: [`${VENDOR_CONFIG.MEDIA_OCEAN.ERROR_MESSAGES.ITEM_PROCESS_ERROR} ${item.name}`]
                            }]);
                        }
                    }
                });
            }

            return { 
                name: VENDOR_CONFIG.MEDIA_OCEAN.NAME, 
                items: Array.from(backupResult)
            };
        } catch (error) {
            AlertMessagesService.showMultipleAlerts([{
                type: 'danger',
                retryBtnText: 'Retry',
                retryBtnEvent: VendorMappingService.getMediaOceanData,
                title: 'Error fetching MediaOcean data',
                bodyMsgArr: [error?.message || 'Unknown error occurred']
            }]);
            return { name: VENDOR_CONFIG.MEDIA_OCEAN.NAME, items: [] };
        }
    },

    /**
     * Deletes a vendor mapping.
     * @function deleteVendorMapping
     * @returns {object}
     * Object, further documentation available at:
     * https://devmediaops-api.annalect.com/dev/docs
    */ 
    deleteVendorMapping: (deleteData) => {
        return axios
            .delete(
                Constants.vendorMappingUrl + "?vendorMappingId=" + deleteData.vendorMappingId,
                {
                    headers: {
                        ansid: authansid,
                        clientid: Utils.getRouteParam("clientid")
                    }
                }
            )
            .then(
                (response) => {
                    return response;
                }
            );
    },
}
